import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import Box from '@mui/material/Box'
import SpeedDial from '@mui/material/SpeedDial'
import SpeedDialIcon from '@mui/material/SpeedDialIcon'
import SpeedDialAction from '@mui/material/SpeedDialAction'
import EditIcon from '@mui/icons-material/Edit'
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded'

import type { PDFDocumentProxy } from 'pdfjs-dist'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.js', import.meta.url).toString()

type PdfViewerProps = {
  onError?: (error?: any) => void
  src: string
}

export default function PdfViewer({ onError, src }: PdfViewerProps) {
  const { t } = useTranslation()

  const [numPages, setNumPages] = useState<number>()
  const [scale, setScale] = useState<number>(1)

  const actions = useMemo(
    () => [
      {
        icon: <ZoomInRoundedIcon />,
        name: 'zoomIn',
        onClick: () => {
          setScale((prevScale) => (prevScale > 3 ? prevScale : prevScale + 0.2))
        },
        tooltip: t('component.ui.pdfViewer.zoomInButton.tooltip'),
      },
      {
        icon: <ZoomOutRoundedIcon />,
        name: 'zoomOut',
        onClick: () => {
          setScale((prevScale) => (prevScale < 0.5 ? prevScale : prevScale - 0.2))
        },
        tooltip: t('component.ui.pdfViewer.zoomOutButton.tooltip'),
      },
    ],
    [t]
  )

  function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy): void {
    setNumPages(nextNumPages)
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Document file={src} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onError}>
        {Array.from(new Array(numPages), (el, index) => (
          <Page key={`page_${index + 1}`} pageNumber={index + 1} scale={scale} />
        ))}
      </Document>
      <SpeedDial
        ariaLabel="pdf-tools"
        direction="up"
        sx={{ position: 'fixed', bottom: '24px', right: '24px' }}
        icon={<SpeedDialIcon openIcon={<EditIcon />} />}
      >
        {actions.map((action) => (
          <SpeedDialAction key={action.name} icon={action.icon} onClick={action.onClick} tooltipTitle={action.tooltip} />
        ))}
      </SpeedDial>
    </Box>
  )
}
